import type { ColDef } from 'ag-grid-community'
import { useEffect, useMemo, useState } from 'react'

import { useGetProduct, useGetProductCategory, useGetProductGroup } from '@/api/react-query'
import { Text } from '@/components/base/text'
import { Pagination } from '@/components/table/pagination'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'

const PAGE_SIZE = 10

export type ApplicationTargetRow = {
	id: string
	uuid: string
	quantity?: number
	kind?: 'group' | 'product' | 'category'
}

export const toGroupTargetRows = (
	entries: { productGroupUuid?: string; productCategoryUuid?: string; quantity?: number }[]
): ApplicationTargetRow[] =>
	entries.flatMap<ApplicationTargetRow>((entry, index) => {
		if (entry.productCategoryUuid) {
			return [
				{
					id: `category-${entry.productCategoryUuid}-${index}`,
					uuid: entry.productCategoryUuid,
					quantity: entry.quantity,
					kind: 'category' as const
				}
			]
		}
		if (entry.productGroupUuid) {
			return [
				{
					id: `group-${entry.productGroupUuid}-${index}`,
					uuid: entry.productGroupUuid,
					quantity: entry.quantity,
					kind: 'group' as const
				}
			]
		}
		return []
	})

export const toUuidTargetRows = (uuids: string[]): ApplicationTargetRow[] =>
	uuids.map((uuid, index) => ({ id: `${uuid}-${index}`, uuid }))

const ProductGroupNameCell = ({ data }: { data?: ApplicationTargetRow }) => {
	const group = useGetProductGroup({ groupUuid: data?.uuid ?? '' }, { enabled: !!data?.uuid })
	return <span className='truncate'>{group.data?.name ?? data?.uuid}</span>
}

const ProductNameCell = ({ data }: { data?: ApplicationTargetRow }) => {
	const product = useGetProduct({ productUuid: data?.uuid ?? '' }, { enabled: !!data?.uuid })
	return <span className='truncate'>{product.data?.title ?? data?.uuid}</span>
}

const CategoryNameCell = ({ data }: { data?: ApplicationTargetRow }) => {
	const category = useGetProductCategory({ categoryUuid: data?.uuid ?? '' }, { enabled: !!data?.uuid })
	return <span className='truncate'>{category.data?.name ?? data?.uuid}</span>
}

const TargetNameCell = ({ data, kind }: { data?: ApplicationTargetRow; kind: ApplicationTargetsTableKind }) => {
	const rowKind = data?.kind ?? kind
	if (rowKind === 'category') return <CategoryNameCell data={data} />
	if (rowKind === 'product') return <ProductNameCell data={data} />
	return <ProductGroupNameCell data={data} />
}

type ApplicationTargetsTableKind = 'group' | 'product' | 'category'

export function ApplicationTargetsTable({
	rows,
	kind,
	showQuantity,
	heading
}: {
	rows: ApplicationTargetRow[]
	kind: ApplicationTargetsTableKind
	showQuantity?: boolean
	heading?: string
}) {
	const { L } = useI18n()
	const [offset, setOffset] = useState(0)

	useEffect(() => {
		if (offset >= rows.length) {
			setOffset(0)
		}
	}, [offset, rows.length])

	const page = rows.slice(offset, offset + PAGE_SIZE)

	const columnDefs = useMemo<ColDef<ApplicationTargetRow>[]>(() => {
		const columns: ColDef<ApplicationTargetRow>[] = [
			{
				headerName: L.Fields.Name(),
				field: 'uuid',
				flex: 2,
				minWidth: 160,
				cellRenderer: (params: { data?: ApplicationTargetRow }) => (
					<TargetNameCell data={params.data} kind={kind} />
				)
			}
		]
		if (showQuantity) {
			columns.push({
				headerName: L.Promotions.Create.ItemCount(),
				field: 'quantity',
				flex: 1,
				minWidth: 100,
				valueGetter: ({ data }) => data?.quantity ?? 1
			})
		}
		return columns
	}, [L, kind, showQuantity])

	if (rows.length === 0) {
		return null
	}

	return (
		<div className='min-w-0'>
			{heading ? (
				<div className='px-3 py-2 sm:px-6 sm:py-3'>
					<Text size='small' weight='plus' leading='compact' className='text-ui-fg-subtle'>
						{heading}
					</Text>
				</div>
			) : null}
			<Table<ApplicationTargetRow>
				className='w-full'
				data={page}
				columnUtilities={{}}
				columnDefs={columnDefs}
				domLayout='autoHeight'
				animateRows
				rowSelection={{
					mode: 'singleRow',
					checkboxes: false,
					enableClickSelection: false,
					enableSelectionWithoutKeys: false
				}}
				suppressCellFocus
				getRowId={row => row.id}
				rowToRouterConfig={row => {
					const rowKind = row.kind ?? kind
					if (rowKind === 'group') {
						return { to: '/product-groups/$groupUuid', params: { groupUuid: row.uuid } }
					}
					if (rowKind === 'category') {
						return { to: '/product-categories/$categoryUuid', params: { categoryUuid: row.uuid } }
					}
					return { to: '/products/$productUuid', params: { productUuid: row.uuid } }
				}}
			/>
			{rows.length > PAGE_SIZE ? (
				<div className='px-3 py-2 sm:px-6 sm:py-4'>
					<Pagination totalCount={rows.length} limit={PAGE_SIZE} offset={offset} onOffsetChange={setOffset} />
				</div>
			) : null}
		</div>
	)
}
